/**
 * Utility functions for date and season handling
 * Can be used by both client-side composables and server-side API endpoints
 */

/**
 * Get current season based on month
 * @param locale - The locale for season names (hu, ro, sk)
 * @returns The current season name in the specified language
 */
export const getCurrentSeason = (locale: string): string => {
  const month = new Date().getMonth() + 1; // getMonth() returns 0-11

  // Season names in different languages
  const seasonNames: { [key: string]: { [key: string]: string } } = {
    hu: {
      spring: "tavasz",
      summer: "nyár",
      autumn: "ősz",
      winter: "tél",
    },
    ro: {
      spring: "primăvară",
      summer: "vară",
      autumn: "toamnă",
      winter: "iarnă",
    },
    sk: {
      spring: "jar",
      summer: "leto",
      autumn: "jeseň",
      winter: "zima",
    },
  };

  const seasons = seasonNames[locale] || seasonNames.hu;

  if (month >= 3 && month <= 5) {
    return seasons.spring;
  } else if (month >= 6 && month <= 8) {
    return seasons.summer;
  } else if (month >= 9 && month <= 11) {
    return seasons.autumn;
  } else {
    return seasons.winter;
  }
};

/**
 * Get formatted current date in the specified locale
 * @param locale - The locale for date formatting (hu, ro, sk)
 * @returns Formatted date string with year, month, day, and weekday
 */
export const getFormattedCurrentDate = (locale: string): string => {
  const now = new Date();

  // Map locale to date formatting locale
  const dateLocaleMap: { [key: string]: string } = {
    hu: "hu-HU",
    ro: "ro-RO",
    sk: "sk-SK",
  };
  const dateLocale = dateLocaleMap[locale] || "hu-HU";

  return now.toLocaleDateString(dateLocale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "long",
  });
};

/**
 * Get current date and season information for chat context
 * @param locale - The locale for formatting (hu, ro, sk)
 * @returns Object containing formatted date and season
 */
export const getCurrentDateAndSeason = (
  locale: string,
): { date: string; season: string } => {
  return {
    date: getFormattedCurrentDate(locale),
    season: getCurrentSeason(locale),
  };
};
